import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Chip,
  Paper,
  Stack,
  useTheme,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getFeaturedProducts, getNewProducts, categories } from '../data/products';
import { useLanguage } from '../context/LanguageContext';

const Home: React.FC = () => {
  const theme = useTheme();
  const { t } = useLanguage();
  const featuredProducts = getFeaturedProducts();
  const newProducts = getNewProducts();

  const features = [
    {
      icon: <ShippingIcon sx={{ fontSize: 24 }} />,
      title: t.home.features.fastDelivery.title,
      description: t.home.features.fastDelivery.description,
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 24 }} />,
      title: t.home.features.securePayment.title,
      description: t.home.features.securePayment.description,
    },
    {
      icon: <SupportIcon sx={{ fontSize: 24 }} />,
      title: t.home.features.support.title,
      description: t.home.features.support.description,
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 1,
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '@keyframes scroll': {
                '0%': {
                  transform: 'translateX(100%)',
                },
                '100%': {
                  transform: 'translateX(-100%)',
                },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                animation: 'scroll 20s linear infinite',
                whiteSpace: 'nowrap',
                color: 'white',
                '@media (max-width: 600px)': {
                  fontSize: '0.875rem',
                },
              }}
            >
              {t.home.heroSubtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Call to Action Button */}
      <Box sx={{ py: 2, textAlign: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/products"
            startIcon={<CartIcon />}
            sx={{
              bgcolor: 'secondary.main',
              color: 'white',
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {t.common.shopNow}
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 1 }}>
        <Grid container spacing={1}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                sx={{
                  p: 1,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 0.5 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Categories Section */}
      <Box sx={{ bgcolor: 'background.default', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom align="center">
            {t.home.categories.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 6 }}>
            {t.home.categories.subtitle}
          </Typography>
          
          <Grid container spacing={3}>
            {categories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Card
                  component={RouterLink}
                  to={`/products?category=${category.id}`}
                  sx={{
                    height: '100%',
                    textDecoration: 'none',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Typography variant="h1" sx={{ mb: 2 }}>
                      {category.icon}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {category.nameFr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.descriptionFr}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Products */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h2" sx={{ flexGrow: 1 }}>
            {t.home.featuredProducts.title}
          </Typography>
          <Button
            component={RouterLink}
            to="/products"
            variant="outlined"
            endIcon={<TrendingIcon />}
          >
            {t.common.viewAll}
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          {featuredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                component={RouterLink}
                to={`/product/${product.id}`}
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image}
                  alt={product.nameFr}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography gutterBottom variant="h6" component="h3">
                    {product.nameFr}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {product.descriptionFr}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Rating value={product.rating} precision={0.1} size="small" readOnly />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({product.reviews})
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {product.price.toLocaleString()} FCFA
                  </Typography>
                  <Chip
                    label={t.common.featured}
                    color="secondary"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  {product.stock < 50 && (
                    <Chip
                      label={t.common.lowStock}
                      color="warning"
                      size="small"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* New Products */}
      {newProducts.length > 0 && (
        <Box sx={{ bgcolor: 'background.default', py: 6 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Typography variant="h4" component="h2" sx={{ flexGrow: 1 }}>
                {t.home.newProducts.title}
              </Typography>
              <Button
                component={RouterLink}
                to="/products"
                variant="outlined"
                endIcon={<TrendingIcon />}
              >
                {t.common.viewAll}
              </Button>
            </Box>
            
            <Grid container spacing={3}>
              {newProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card
                    component={RouterLink}
                    to={`/product/${product.id}`}
                    sx={{
                      height: '100%',
                      textDecoration: 'none',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-4px)' },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={product.image}
                        alt={product.nameFr}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Chip
                        label={t.common.new}
                        color="secondary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography gutterBottom variant="h6" component="h3">
                        {product.nameFr}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {product.descriptionFr}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Rating value={product.rating} precision={0.1} size="small" readOnly />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({product.reviews})
                        </Typography>
                      </Box>
                      <Typography variant="h6" color="primary">
                        {product.price.toLocaleString()} FCFA
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Call to Action */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 3,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'white' }}>
            {t.home.callToAction.title}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, opacity: 0.9, color: 'white' }}>
            {t.home.callToAction.subtitle}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
            <Button
              variant="contained"
              size="medium"
              component={RouterLink}
              to="/products"
              startIcon={<CartIcon />}
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                '&:hover': { bgcolor: 'secondary.dark' },
              }}
            >
              {t.common.browseProducts}
            </Button>
            <Button
              variant="outlined"
              size="medium"
              component={RouterLink}
              to="/contact"
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {t.common.contactUs}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 